import type { InternalNetworkConfig } from '../interceptor/types';
import { fetchApi } from './index';

const UNAUTHORIZED_CODE = 'UAG-005';
const DEFAULT_MAX_ATTEMPTS = 2;

export type RetryReason = 'network' | 'unauthorized' | 'rate_limit' | 'server_error' | 'client_error';

export type RetryExhaustionInfo = {
    attempts: number;
    reason: RetryReason;
    status: number | null;
};

export type RunWithRetryOptions<T> = {
    url: string;
    init: RequestInit;
    networkConfig?: InternalNetworkConfig;
    parse: (res: Response) => Promise<T>;
    maxAttempts?: number;
    onExhausted?: (info: RetryExhaustionInfo) => void;
};

const categorizeStatus = (status: number): RetryReason => {
    if (status === 401) return 'unauthorized';
    if (status === 429 || status === 503) return 'rate_limit';
    if (status >= 500) return 'server_error';
    return 'client_error';
};

/**
 * Generic N-attempt retry around `fetchApi`. Retries on:
 *   - thrown errors (network / fetch failures)
 *   - non-OK HTTP responses
 *   - response body with `code: 'UAG-005'`
 *
 * Returns the parsed value on success, or `null` if every attempt fails.
 * On exhaustion, invokes `onExhausted` once with a categorical reason so the
 * caller can emit a structured log / RUM event for Datadog monitoring.
 */
export const runWithRetry = async <T>({
    url,
    init,
    networkConfig,
    parse,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    onExhausted
}: RunWithRetryOptions<T>): Promise<T | null> => {
    const options = networkConfig ? { networkConfig } : undefined;

    let lastReason: RetryReason = 'network';
    let lastStatus: number | null = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            const res = await fetchApi(url, init, options);
            lastStatus = res.status;

            if (!res.ok) {
                lastReason = categorizeStatus(res.status);
                continue;
            }

            const value = await parse(res);
            if ((value as { code?: string } | null)?.code === UNAUTHORIZED_CODE) {
                lastReason = 'unauthorized';
                continue;
            }

            return value;
        } catch {
            lastReason = 'network';
            lastStatus = null;
        }
    }

    onExhausted?.({ attempts: maxAttempts, reason: lastReason, status: lastStatus });
    return null;
};
